/**
 * Origem das visitas (marketing): de que campanha, anúncio ou site veio quem
 * se cadastra. Corre no arranque da app, ANTES de o router mexer no URL.
 *
 *   - Lê os utm_* (source, medium, campaign, content, term), o tipo de clique
 *     de anúncio (gclid/fbclid/ttclid/msclkid — só o tipo, nunca o valor), o
 *     site de onde veio (só o domínio) e a página de entrada.
 *   - Guarda no browser o PRIMEIRO contacto (renovado ao fim de 90 dias) e o
 *     ÚLTIMO contacto com origem (visitas directas não o apagam).
 *   - Logo a seguir ao cadastro, a app envia-os uma vez (POST /api/me/acquisition);
 *     o servidor só os grava para contas acabadas de criar.
 *
 * É análise própria, na mesma origem: nada vai para terceiros.
 */
export const CLICK_IDS = ['gclid', 'fbclid', 'ttclid', 'msclkid'] as const;

export interface Touch {
	source?: string;
	medium?: string;
	campaign?: string;
	content?: string;
	term?: string;
	clickId?: (typeof CLICK_IDS)[number];
	referrerHost?: string;
	landingPath?: string;
	at: number;
}

export interface Attribution {
	first: Touch;
	last: Touch | null;
}

const KEY = 'mates:origem:v1';
const SENT_PREFIX = 'mates:origem:enviada:';
const FIRST_TOUCH_TTL_MS = 90 * 86_400_000;
/** De volta de um login ou de uma instalação (Google, GitHub…): não é uma origem de marketing. */
const IGNORED_REFERRERS = ['accounts.google.com', 'accounts.youtube.com', 'github.com', 'appleid.apple.com', 'login.microsoftonline.com', 'login.live.com'];

type Storage = Pick<globalThis.Storage, 'getItem' | 'setItem'>;

function defaultStorage(): Storage | null {
	try {
		return window.localStorage;
	} catch {
		return null; // modo privado, cookies bloqueados…
	}
}

const param = (params: URLSearchParams, name: string) => params.get(name)?.trim().slice(0, 100) || undefined;

/** A visita deste URL: campanha, clique de anúncio, site de origem e página de entrada. */
export function touchFromLocation(href: string, referrer: string, now: number): Touch {
	const url = new URL(href);
	const params = url.searchParams;
	const touch: Touch = { at: now, landingPath: url.pathname.slice(0, 200) };
	for (const key of ['source', 'medium', 'campaign', 'content', 'term'] as const) {
		const value = param(params, `utm_${key}`);
		if (value) touch[key] = value;
	}
	const click = CLICK_IDS.find((id) => params.has(id));
	if (click) touch.clickId = click;
	try {
		const host = referrer ? new URL(referrer).hostname.toLowerCase() : '';
		const ignored = IGNORED_REFERRERS.some((h) => host === h || host.endsWith(`.${h}`));
		if (host && host !== url.hostname.toLowerCase() && !ignored) touch.referrerHost = host;
	} catch {
		/* referrer inválido: ignora-se */
	}
	return touch;
}

export function hasOrigin(t: Touch | null | undefined): boolean {
	return Boolean(t && (t.source || t.medium || t.campaign || t.clickId || t.referrerHost));
}

function read(storage: Storage | null): Attribution | null {
	try {
		const raw = storage?.getItem(KEY);
		const parsed = raw ? (JSON.parse(raw) as Attribution) : null;
		return parsed && typeof parsed.first?.at === 'number' ? parsed : null;
	} catch {
		return null;
	}
}

/** Regista a visita actual (no arranque da app). */
export function captureTouch(options: { href?: string; referrer?: string; now?: number; storage?: Storage | null } = {}): Attribution {
	const now = options.now ?? Date.now();
	const storage = options.storage === undefined ? defaultStorage() : options.storage;
	const touch = touchFromLocation(options.href ?? window.location.href, options.referrer ?? document.referrer, now);
	const stored = read(storage);
	const next: Attribution = {
		// O primeiro contacto fica (até 90 dias); depois disso, conta o actual como novo começo.
		first: stored && now - stored.first.at < FIRST_TOUCH_TTL_MS ? stored.first : touch,
		// O último com origem: uma visita directa não apaga a campanha que trouxe a pessoa.
		last: hasOrigin(touch) ? touch : (stored?.last ?? null)
	};
	try {
		storage?.setItem(KEY, JSON.stringify(next));
	} catch {
		/* sem espaço ou bloqueado: a origem desta visita perde-se */
	}
	return next;
}

/** O que enviar depois do cadastro (null se nada foi captado neste browser). */
export function pendingAttribution(storage: Storage | null = defaultStorage()): Attribution | null {
	return read(storage);
}

export function attributionSent(userId: string, storage: Storage | null = defaultStorage()): boolean {
	try {
		return storage?.getItem(SENT_PREFIX + userId) === '1';
	} catch {
		return true; // sem armazenamento, não se tenta repetir
	}
}

export function markAttributionSent(userId: string, storage: Storage | null = defaultStorage()): void {
	try {
		storage?.setItem(SENT_PREFIX + userId, '1');
	} catch {
		/* ignorado */
	}
}
